/**
 * SocialSignInButtons — "Continue with Google / Apple / Microsoft / LinkedIn".
 *
 * The first provider on its own full-width row ("Continue with LinkedIn"), the
 * others side by side under it, each with its brand's name — the same outline
 * surface for all — the form every provider's sign-in branding
 * guidelines allow (Google "neutral", Apple "white with outline", Microsoft
 * "light", LinkedIn). The providers and their order are the caller's
 * (`SOCIAL_PROVIDERS` from @tonnie/foundation); the labels are passed in
 * translated, like every other text this package renders.
 *
 * While one provider is redirecting, its button spins and all are disabled —
 * a second click would start a second round trip.
 */

import {
  AppleIcon,
  GoogleIcon,
  LinkedInIcon,
  MicrosoftIcon,
  type CustomIconProps,
} from '@tonnie/assets';
import { SOCIAL_PROVIDER_NAME, type SocialProvider } from '@tonnie/foundation';
import { Loader2 } from 'lucide-react';
import type { ComponentType } from 'react';

import { THIRD_PARTY_BRAND_CLASSES } from '../lib/third-party-brand-classes';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';

const MARK: Record<SocialProvider, ComponentType<CustomIconProps>> = {
  google: GoogleIcon,
  apple: AppleIcon,
  microsoft: MicrosoftIcon,
  linkedin: LinkedInIcon,
};

/** LinkedIn's mark is drawn in currentColor: its brand blue (white on dark, per its guidelines). */
const MARK_CLASS: Partial<Record<SocialProvider, string>> = {
  linkedin: THIRD_PARTY_BRAND_CLASSES.linkedinText,
};

/** A provider's own mark, decorative (the name is always written beside it). */
export function SocialProviderMark({
  provider,
  size = 16,
  className,
}: {
  provider: SocialProvider;
  size?: number;
  className?: string;
}) {
  const Mark = MARK[provider];
  return <Mark size={size} className={cn(MARK_CLASS[provider], className)} aria-hidden="true" />;
}

export interface SocialSignInButtonsProps {
  providers: readonly SocialProvider[];
  /** "Continue with Google", … — translated by the caller. */
  labels: Record<SocialProvider, string>;
  onSelect: (provider: SocialProvider) => void;
  /** The provider whose redirect is starting; every button waits for it. */
  pending?: SocialProvider | null;
  disabled?: boolean;
  /** Test id prefix: `${testIdPrefix}-${provider}`. */
  testIdPrefix: string;
  className?: string;
}

export function SocialSignInButtons({
  providers,
  labels,
  onSelect,
  pending = null,
  disabled = false,
  testIdPrefix,
  className,
}: SocialSignInButtonsProps) {
  const [lead, ...rest] = providers;

  const button = (provider: SocialProvider, compact: boolean) => (
    <Button
      key={provider}
      type="button"
      variant="outline"
      className={cn(
        'h-11 w-full justify-center gap-2 font-medium',
        compact ? 'text-sm' : 'text-base'
      )}
      disabled={disabled || pending !== null}
      aria-busy={pending === provider}
      // The compact buttons show the brand name alone; the full phrase stays
      // their accessible name.
      aria-label={compact ? labels[provider] : undefined}
      onClick={() => onSelect(provider)}
      data-testid={`${testIdPrefix}-${provider}`}
    >
      {pending === provider ? (
        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      ) : (
        <SocialProviderMark provider={provider} size={compact ? 15 : 18} />
      )}
      {compact ? SOCIAL_PROVIDER_NAME[provider] : labels[provider]}
    </Button>
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {lead && button(lead, false)}
      {rest.length > 0 && (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${rest.length}, minmax(0, 1fr))` }}
        >
          {rest.map((provider) => button(provider, true))}
        </div>
      )}
    </div>
  );
}
